import type { TrackingClient } from '../types';

export type UnloadPageLeaveState = {
  pageId: string;
  enterAt: number;
  routeName?: string;
};

export type BindUnloadPageLeaveResult = {
  /** 移除监听 */
  unbind: () => void;
  /** 路由切换或新页面开始时重置离开保护，避免误跳过后续 close/hide */
  reset: () => void;
};

/**
 * 监听页面隐藏 / 关闭，上报 page_leave（hide / close），并即时发送。
 *
 * 去重策略：
 * - 切标签等仅 hidden：visibilitychange → 延迟上报 hide
 * - 关页/刷新/离开：pagehide 抢先取消 hide，上报 close
 * - 同一页面周期内 hide/close 只上报一次；重新可见后允许再次上报
 */
export function bindUnloadPageLeave(
  client: TrackingClient,
  getState: () => UnloadPageLeaveState,
  onResume?: () => void
): BindUnloadPageLeaveResult {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { unbind: () => undefined, reset: () => undefined };
  }

  let leaveSent = false;
  let hideTimer: ReturnType<typeof setTimeout> | null = null;

  const clearHideTimer = () => {
    if (hideTimer != null) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  };

  const reportLeave = (leaveType: 'hide' | 'close') => {
    if (leaveSent) return;
    const { pageId, enterAt, routeName } = getState();
    if (!pageId) return;
    leaveSent = true;
    // hide：页面仍在，用 fetch 即时发送（Network 可见）
    // close：页面卸载中，用 sendBeacon 更稳
    client.pageLeave(
      {
        page_id: pageId,
        duration_ms: Math.max(0, Date.now() - enterAt),
        leave_type: leaveType,
        route_name: routeName,
      },
      { immediate: true, preferBeacon: leaveType === 'close' }
    );
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      // 延迟一拍：若紧接着 pagehide（关页），由 close 上报；纯切标签则走 hide
      clearHideTimer();
      hideTimer = setTimeout(() => {
        hideTimer = null;
        reportLeave('hide');
      }, 0);
      return;
    }

    if (document.visibilityState === 'visible') {
      clearHideTimer();
      if (leaveSent) {
        leaveSent = false;
        onResume?.();
      }
    }
  };

  const onPageHide = () => {
    clearHideTimer();
    reportLeave('close');
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onPageHide);

  return {
    unbind() {
      clearHideTimer();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
    },
    reset() {
      clearHideTimer();
      leaveSent = false;
    },
  };
}
